/**
 * English UI strings. Status entries follow HANDOFF §2.3 verbatim: `status.<domain>.<status>.admin | .portal | .help`.
 * A missing `.portal` key means the status is never shown in the portal (StatusBadge falls back to the admin label).
 */
export const en: Record<string, string> = {
  // ---- identity
  "status.identity.pre_hire.admin": "Starts soon",
  "status.identity.pre_hire.portal": "Starts {date}",
  "status.identity.pre_hire.help": "Registered but not started yet; access can be prepared before the start date.",
  "status.identity.active.admin": "Active",
  "status.identity.active.help": "Works here and keeps the access they have.",
  "status.identity.leave.admin": "On leave",
  "status.identity.leave.portal": "On leave",
  "status.identity.leave.help": "Temporarily away; accounts may be disabled until they return.",
  "status.identity.offboarding.admin": "Leaving",
  "status.identity.offboarding.portal": "Leaving",
  "status.identity.offboarding.help": "On the way out; access is being removed.",
  "status.identity.inactive.admin": "Left",
  "status.identity.inactive.help": "No longer works here; no new access can be added.",

  // ---- request
  "status.request.submitted.admin": "Submitted",
  "status.request.submitted.portal": "Sent",
  "status.request.submitted.help": "Registered and on its way to the first approver.",
  "status.request.pending_approval.admin": "Waiting for approval",
  "status.request.pending_approval.portal": "Waiting for {name}",
  "status.request.pending_approval.help": "One or more people still have to decide.",
  "status.request.approved.admin": "Approved",
  "status.request.approved.portal": "Approved – being set up",
  "status.request.approved.help": "All decisions are made; the access is now being set up.",
  "status.request.rejected.admin": "Rejected",
  "status.request.rejected.portal": "Not approved",
  "status.request.rejected.help": "An approver said no; their reason is shown.",
  "status.request.completed.admin": "Completed",
  "status.request.completed.portal": "Access ready",
  "status.request.completed.help": "The access is in place in the systems.",
  "status.request.failed.admin": "Failed",
  "status.request.failed.portal": "Couldn't be set up",
  "status.request.failed.help": "Setting up the access failed; an admin must look at why.",
  "status.request.cancelled.admin": "Cancelled",
  "status.request.cancelled.portal": "Cancelled",
  "status.request.cancelled.help": "Withdrawn before a decision.",

  // ---- step
  "status.step.waiting.admin": "Not started",
  "status.step.waiting.help": "An earlier approval step has to finish first.",
  "status.step.pending.admin": "Waiting for {name}",
  "status.step.pending.portal": "Waiting for you",
  "status.step.pending.help": "This person has to decide now.",
  "status.step.skipped.admin": "Skipped",
  "status.step.skipped.portal": "Skipped",
  "status.step.skipped.help": "Not needed, for example because the approver asked for it.",
  "status.step.escalated.admin": "Escalated",
  "status.step.escalated.portal": "Passed to {name}",
  "status.step.escalated.help": "Overdue, so it was passed to someone else to decide.",
  "status.step.approved.admin": "Approved",
  "status.step.approved.portal": "Approved",
  "status.step.approved.help": "This person approved the request.",
  "status.step.rejected.admin": "Rejected",
  "status.step.rejected.portal": "Not approved",
  "status.step.rejected.help": "This person said no; their reason is shown.",

  // ---- account
  "status.account.present.admin": "In place",
  "status.account.present.help": "Exists in the system as expected.",
  "status.account.pending_add.admin": "Being added",
  "status.account.pending_add.portal": "Being set up",
  "status.account.pending_add.help": "The platform has asked the system to add it.",
  "status.account.pending_remove.admin": "Being removed",
  "status.account.pending_remove.portal": "Being removed",
  "status.account.pending_remove.help": "The platform has asked the system to remove it.",
  "status.account.orphan.admin": "No owner",
  "status.account.orphan.help": "Not linked to any person; someone must claim it or remove it.",
  "status.account.external.admin": "Granted outside the platform",
  "status.account.external.help": "Exists in the system but was not granted through the platform.",
  "status.account.disabled.admin": "Disabled",
  "status.account.disabled.portal": "Disabled",
  "status.account.disabled.help": "The account exists but cannot be used.",
  "status.account.locked.admin": "Locked",
  "status.account.locked.portal": "Locked",
  "status.account.locked.help": "Too many failed sign-ins or locked by an admin; it can be unlocked.",

  // ---- syncRun
  "status.syncRun.queued.admin": "Queued",
  "status.syncRun.queued.help": "Starts when a worker or agent is free.",
  "status.syncRun.running.admin": "Running",
  "status.syncRun.running.help": "Reading, planning or applying changes now.",
  "status.syncRun.awaiting_review.admin": "Waiting for review",
  "status.syncRun.awaiting_review.help": "Planned changes exceed a safety threshold; nothing is applied until someone applies or cancels.",
  "status.syncRun.held.admin": "Held back",
  "status.syncRun.held.help": "Deletes over the rate limit wait for a person to apply them.",
  "status.syncRun.succeeded.admin": "Completed",
  "status.syncRun.succeeded.help": "All planned changes were applied.",
  "status.syncRun.failed.admin": "Failed",
  "status.syncRun.failed.help": "The run stopped with an error; see the step and message.",
  "status.syncRun.cancelled.admin": "Cancelled",
  "status.syncRun.cancelled.help": "Stopped by a person before anything was applied.",
  "status.syncRun.dry_run.admin": "Dry run – nothing changed",
  "status.syncRun.dry_run.help": "Changes were planned for review only.",

  // ---- sod
  "status.sod.blocked.admin": "Not allowed together",
  "status.sod.blocked.portal": "Not allowed together",
  "status.sod.blocked.help": "This combination is forbidden by policy and cannot be requested.",
  "status.sod.open.admin": "Open conflict",
  "status.sod.open.portal": "Conflict",
  "status.sod.open.help": "Someone holds both sides of a policy; the owner must remove access or accept the risk.",
  "status.sod.mitigated.admin": "Accepted until {date}",
  "status.sod.mitigated.help": "Risk accepted with a reason until the date; then it reopens.",
  "status.sod.resolved.admin": "Resolved",
  "status.sod.resolved.help": "No longer a conflict, because access was removed or the policy changed.",

  // ---- certification
  "status.certification.undecided.admin": "Not decided",
  "status.certification.undecided.portal": "To do",
  "status.certification.undecided.help": "Waiting for the reviewer's decision.",
  "status.certification.keep.admin": "Keep",
  "status.certification.keep.portal": "Keep",
  "status.certification.keep.help": "The reviewer confirmed the access is still needed.",
  "status.certification.revoke.admin": "Revoke",
  "status.certification.revoke.portal": "Remove",
  "status.certification.revoke.help": "Will be removed when the review closes, or right away if set up so.",
  "status.certification.reassign.admin": "Reassigned",
  "status.certification.reassign.portal": "Passed on",
  "status.certification.reassign.help": "Sent to another reviewer, or an owner was proposed for the account.",

  // ---- remediation
  "status.remediation.pending.admin": "Removal in progress",
  "status.remediation.pending.help": "The platform has asked the system to remove the access.",
  "status.remediation.done.admin": "Removed",
  "status.remediation.done.help": "The access was removed in the system.",
  "status.remediation.failed.admin": "Removal failed",
  "status.remediation.failed.help": "The access could not be removed in the system; an admin must act.",

  // ---- agent
  "status.agent.online.admin": "Online",
  "status.agent.online.help": "The agent is sending heartbeats.",
  "status.agent.offline.admin": "Offline",
  "status.agent.offline.help": "No heartbeat; work for its systems is paused.",

  // ---- role
  "status.role.draft.admin": "Draft",
  "status.role.draft.help": "Being prepared; nobody can request or receive it yet.",
  "status.role.published.admin": "Published",
  "status.role.published.help": "Live; can be assigned and, if requestable, requested.",
  "status.role.retired.admin": "Retired",
  "status.role.retired.help": "No longer offered; assignments made by rules are removed.",

  // ---- assignment
  "status.assignment.active.admin": "Active",
  "status.assignment.active.portal": "Active",
  "status.assignment.active.help": "The access is in place and in use.",
  "status.assignment.pending.admin": "Being set up",
  "status.assignment.pending.portal": "Being set up",
  "status.assignment.pending.help": "Granted, and the accounts and memberships are being created now.",
  "status.assignment.expiring.admin": "Ends in {days} days",
  "status.assignment.expiring.portal": "Ends in {days} days",
  "status.assignment.expiring.help": "Time-limited access is about to end; it can be extended.",
  "status.assignment.expired.admin": "Expired",
  "status.assignment.expired.portal": "Ended",
  "status.assignment.expired.help": "The end date passed and the access was removed.",
  "status.assignment.revoked.admin": "Revoked",
  "status.assignment.revoked.portal": "Removed",
  "status.assignment.revoked.help": "The access was taken away by a review, a rule or an admin.",

  // ---- job (background work)
  "status.job.queued.admin": "Queued",
  "status.job.queued.help": "Waits for a free worker.",
  "status.job.running.admin": "Running",
  "status.job.running.help": "A worker is processing it now.",
  "status.job.failed.admin": "Failed",
  "status.job.failed.help": "The last attempt ended with an error; it retries by itself until the limit.",
  "status.job.quarantined.admin": "Quarantined",
  "status.job.quarantined.help": "Failed repeatedly and was set aside; it will not retry until an admin resumes it.",
  "status.job.cancelled.admin": "Cancelled",
  "status.job.cancelled.help": "Stopped before it finished.",
  "status.job.succeeded.admin": "Completed",
  "status.job.succeeded.help": "Finished without errors.",

  // ---- hosting domains (tenant, tenantRequest, provisioningJob, release, upgrade, upgradeOutcome, cluster, domain, platformUser)
  "status.tenant.requested.admin": "Requested", "status.tenant.requested.help": "A sign-up exists but nothing has been created yet.",
  "status.tenant.provisioning.admin": "Being set up", "status.tenant.provisioning.help": "The worker is creating the database and the first admin user. Usually takes under a minute.",
  "status.tenant.active.admin": "Active", "status.tenant.active.help": "Users can sign in; jobs and agents run normally.",
  "status.tenant.suspended.admin": "Suspended", "status.tenant.suspended.help": "An operator locked users out. Nothing is deleted; resume to let them back in.",
  "status.tenant.deprovisioning.admin": "Being removed", "status.tenant.deprovisioning.help": "The worker is dropping the database and role. This cannot be stopped.",
  "status.tenant.deleted.admin": "Deleted", "status.tenant.deleted.help": "The database is gone; only the catalogue row and audit trail remain.",
  "status.tenantRequest.email_verification.admin": "Waiting for e-mail confirmation", "status.tenantRequest.email_verification.help": "The requester has not clicked the confirmation link yet. Nothing to do until they have.",
  "status.tenantRequest.pending.admin": "Waiting for decision", "status.tenantRequest.pending.help": "E-mail confirmed; an operator must approve or reject it.",
  "status.tenantRequest.approved.admin": "Approved – being set up", "status.tenantRequest.approved.help": "Approved; the tenant is being provisioned.",
  "status.tenantRequest.rejected.admin": "Rejected", "status.tenantRequest.rejected.help": "An operator said no; the reason was sent to the requester.",
  "status.tenantRequest.provisioned.admin": "Tenant ready", "status.tenantRequest.provisioned.help": "The tenant is active and the owner has been invited.",
  "status.provisioningJob.queued.admin": "Queued", "status.provisioningJob.queued.help": "Starts when the tenancy worker is free.",
  "status.provisioningJob.running.admin": "Running", "status.provisioningJob.running.help": "The worker is working on it now.",
  "status.provisioningJob.succeeded.admin": "Completed", "status.provisioningJob.succeeded.help": "Finished without errors.",
  "status.provisioningJob.failed.admin": "Failed", "status.provisioningJob.failed.help": "Stopped with an error and will not retry on its own. Read the error, fix the cause, then retry.",
  "status.release.draft.admin": "Draft", "status.release.draft.help": "Not visible to tenants yet. Publish it to open the upgrade window.",
  "status.release.available.admin": "Published", "status.release.available.help": "Tenants can upgrade until the deadline; after that the upgrade is forced.",
  "status.release.closed.admin": "Closed", "status.release.closed.help": "The window is closed; tenants still behind are upgraded automatically.",
  "status.upgrade.up_to_date.admin": "Up to date", "status.upgrade.up_to_date.help": "On the newest published release.",
  "status.upgrade.available.admin": "Update available", "status.upgrade.available.help": "A newer release is published; the tenant decides when to upgrade before the deadline.",
  "status.upgrade.scheduled.admin": "Scheduled", "status.upgrade.scheduled.help": "The upgrade runs at the chosen time.",
  "status.upgrade.running.admin": "Upgrading", "status.upgrade.running.help": "Snapshot, migrations and verification are running now.",
  "status.upgrade.failed.admin": "Upgrade failed", "status.upgrade.failed.help": "The upgrade failed and the snapshot was restored. Only a hosting operator can retry it.",
  "status.upgrade.forced.admin": "Forced", "status.upgrade.forced.help": "The deadline passed without a schedule, so the upgrade runs at the next 02:00 in the tenant's time zone.",
  "status.upgrade.blocked.admin": "Needs an intermediate release", "status.upgrade.blocked.help": "The tenant is below the release's minimum version; publish an intermediate release first.",
  "status.upgradeOutcome.succeeded.admin": "Completed", "status.upgradeOutcome.succeeded.help": "Migrated and verified.",
  "status.upgradeOutcome.failed.admin": "Failed", "status.upgradeOutcome.failed.help": "Stopped with an error.",
  "status.upgradeOutcome.rolled_back.admin": "Rolled back", "status.upgradeOutcome.rolled_back.help": "The upgrade failed and the snapshot from before it was restored.",
  "status.cluster.unchecked.admin": "Not checked", "status.cluster.unchecked.help": "No health check in this session yet.",
  "status.cluster.checking.admin": "Checking", "status.cluster.checking.help": "Connecting and running SELECT 1 (4 s timeout).",
  "status.cluster.ok.admin": "Reachable", "status.cluster.ok.help": "The admin connection works.",
  "status.cluster.down.admin": "Unreachable", "status.cluster.down.help": "The admin connection failed; provisioning on this cluster will fail until it is back.",
  "status.cluster.full.admin": "Full", "status.cluster.full.help": "At its tenant limit.",
  "status.domain.verified.admin": "Verified", "status.domain.verified.help": "The TXT record was found; the tenant is reachable on this host name.",
  "status.domain.pending.admin": "Waiting for DNS record", "status.domain.pending.help": "The customer must publish the TXT record; then press Verify.",
  "status.platformUser.active.admin": "Active", "status.platformUser.active.help": "Can sign in.",
  "status.platformUser.must_change_password.admin": "Temporary password", "status.platformUser.must_change_password.help": "Must change the password at the next sign-in.",
  "status.platformUser.disabled.admin": "Disabled", "status.platformUser.disabled.help": "Cannot sign in; sessions were ended.",

  // ---- tenant admin: campaigns, users, API keys, switches, lifecycle, circuit breaker, agent watch and relay
  "status.account.deleted.admin": "Removed", "status.account.deleted.help": "The account no longer exists in the system.",
  "status.agent.disabled.admin": "Disabled", "status.agent.disabled.help": "Switched off by an admin; it will not receive work until enabled again.",
  "status.campaign.draft.admin": "Draft", "status.campaign.draft.help": "Not started; reviewers have not been asked anything yet.",
  "status.campaign.active.admin": "Open", "status.campaign.active.help": "Reviewers are deciding; items without a decision are undecided.",
  "status.campaign.completed.admin": "Closed", "status.campaign.completed.help": "All decisions are in and applied.",
  "status.campaign.cancelled.admin": "Cancelled", "status.campaign.cancelled.help": "Stopped before completion; no decisions were applied.",
  "status.user.active.admin": "Active", "status.user.active.help": "Can sign in to the admin console.",
  "status.user.invited.admin": "Invited", "status.user.invited.help": "Created but has not set a password yet.",
  "status.user.disabled.admin": "Disabled", "status.user.disabled.help": "Cannot sign in; enable to let them back in.",
  "status.apiKey.active.admin": "Active", "status.apiKey.active.help": "Accepted for API calls.",
  "status.apiKey.expired.admin": "Expired", "status.apiKey.expired.help": "Past its end date; create a new key to continue.",
  "status.apiKey.revoked.admin": "Revoked", "status.apiKey.revoked.help": "Withdrawn by an admin; calls with it are rejected.",
  "status.enabled.enabled.admin": "Enabled", "status.enabled.enabled.help": "In use.",
  "status.enabled.disabled.admin": "Disabled", "status.enabled.disabled.help": "Kept but not in use.",
  "status.lifecycle.waiting.admin": "Waiting for the worker", "status.lifecycle.waiting.help": "The event is queued; the worker applies it shortly.",
  "status.lifecycle.processed.admin": "Processed", "status.lifecycle.processed.help": "The worker applied the event.",
  "status.circuit.closed.admin": "Closed", "status.circuit.closed.help": "Calls go through normally.",
  "status.circuit.half_open.admin": "Half open", "status.circuit.half_open.help": "Testing with a few calls after failures; closes again when they succeed.",
  "status.circuit.open.admin": "Open", "status.circuit.open.help": "Too many failures; calls are skipped until the cool-down ends.",
  "status.watch.watching.admin": "Watching", "status.watch.watching.help": "Receiving changes live.",
  "status.watch.starting.admin": "Starting", "status.watch.starting.help": "Connecting to the source.",
  "status.watch.catching-up.admin": "Catching up", "status.watch.catching-up.help": "Replaying changes missed while disconnected.",
  "status.watch.reconnecting.admin": "Reconnecting", "status.watch.reconnecting.help": "Lost the connection and is retrying.",
  "status.watch.paused.admin": "Paused", "status.watch.paused.help": "Stopped on purpose; resume to continue.",
  "status.watch.error.admin": "Error", "status.watch.error.help": "The watch failed; see the last error.",
  "status.watch.stopped.admin": "Stopped", "status.watch.stopped.help": "Not watching this domain.",
  "status.watch.unknown.admin": "Unknown", "status.watch.unknown.help": "The agent has not reported this domain yet.",
  "status.relay.queued.admin": "Queued", "status.relay.queued.help": "Waiting for the agent to pick it up.",
  "status.relay.running.admin": "Running", "status.relay.running.help": "The agent is executing the call.",
  "status.relay.done.admin": "Done", "status.relay.done.help": "The agent answered.",
  "status.relay.failed.admin": "Failed", "status.relay.failed.help": "The agent could not complete the call; see the error.",
  "status.upgrade.downloading.admin": "Downloading", "status.upgrade.downloading.help": "The agent is fetching the new version.",
  "status.upgrade.installing.admin": "Installing", "status.upgrade.installing.help": "The agent is installing the new version and will restart.",
  "status.upgradeOutcome.running.admin": "Running", "status.upgradeOutcome.running.help": "The upgrade is in progress.",

  // ---- tones
  "tone.ok": "Healthy",
  "tone.info": "In progress",
  "tone.warn": "Needs attention",
  "tone.bad": "Blocked",
  "tone.neutral": "Neutral",

  // ---- risk (§2.4)
  "risk.1": "Low",
  "risk.2": "Medium",
  "risk.3": "High",
  "risk.4": "Critical",
  "risk.label": "{level} risk",

  // ---- source of access
  "source.rule": "Automatic",
  "source.request": "Requested",
  "source.manual": "Added by admin",
  "source.inherited": "Included in role",
  "source.import": "Imported",
  "source.external": "Granted outside the platform",

  // ---- planned change actions
  "action.create": "Create",
  "action.update": "Update",
  "action.link": "Link",
  "action.disable": "Disable",
  "action.delete": "Delete",

  // ---- conflicts
  "conflict.prevent": "Not allowed together with {otherSide}, which {holder} has.",
  "conflict.detect": "Conflicts with {otherSide}, which {holder} has.",
  "conflict.mitigated": "Conflicts with {otherSide}, which {holder} has. Accepted until {until}.",

  // ---- general UI
  "ui.close": "Close",
  "ui.cancel": "Cancel",
  "ui.back": "Back",
  "ui.save": "Save",
  "ui.saveDraft": "Save draft",
  "ui.saveAndExit": "Save and exit",
  "ui.discard": "Discard",
  "ui.confirm": "Confirm",
  "ui.details": "Details",
  "ui.search": "Search",
  "ui.clearSearch": "Clear search",
  "ui.loading": "Loading…",
  "ui.copy": "Copy",
  "ui.copied": "Copied",
  "ui.copyDetails": "Copy details",
  "ui.viewOnly": "View only",
  "ui.undo": "Undo",
  "ui.moreFilters": "More filters",
  "ui.all": "All",
  "ui.openMenu": "Open menu",
  "ui.signOut": "Sign out",
  "ui.previousPage": "Previous page",
  "ui.nextPage": "Next page",
  "ui.page": "Page {page} of {pages}",
  "ui.total": "{count} total",
  "ui.nothingHereYet": "Nothing here yet",
  "ui.step": "step {step} of {steps}",
  "ui.steps": "Steps",
  "ui.whatHappened": "What happened",
  "ui.whatToDo": "What to do",
  "ui.ref": "ref {ref}",
  "ui.typeToConfirm": "Type {name} to confirm",
  "ui.keep": "Keep",
  "ui.notAvailable.title": "{section} isn't available to you",
  "ui.notAvailable.body": "Your role ({role}) doesn't include {section}. {how}",
  "ui.notAvailable.home": "Go to Home",
  "ui.notAvailable.copyLink": "Copy link to send an admin",
  "ui.group.blocked": "Blocked",
  "ui.group.attention": "Needs attention",
  "ui.group.later": "Due later",
  "ui.unsavedChanges": "{count} unsaved changes",
  "ui.unsavedChange": "1 unsaved change",
  "ui.errorsToFix": "{count} errors must be fixed before saving",
  "ui.errorToFix": "1 error must be fixed before saving",
  "ui.nothingChangedYet": "Nothing has been changed yet",
  "ui.applyChanges": "Apply {count} changes",
  "ui.cancelRun": "Cancel run",
  "ui.howGotThis": "How {name} got this",
  "ui.cause": "Cause",
  "ui.environment.production": "Production",
  "ui.environment.sandbox": "Sandbox",
  "ui.viewingReadOnly": "Viewing {tenant} · read-only",
  "ui.theme": "Theme",
  "ui.theme.light": "Light",
  "ui.theme.dark": "Dark",
  "ui.theme.system": "System",
  "ui.leavePage": "You have unsaved changes. Leave without saving?",
  "ui.clear": "Clear",
  "ui.change": "Change",
  "ui.noMatches": "No matches",
  "ui.removeItem": "Remove {name}",
  "ui.nothingSelected": "Nothing selected",
  "ui.searchNothing": "Nothing matches “{q}”",
  "ui.accountMenu": "Account menu for {name}",
  "ui.profile": "Profile",
  "ui.language": "Language",
  "ui.pager.showing": "Showing {from}–{to} of {total}",

  // ---- sign-in start page (home realm discovery)
  "ui.chooser.intro": "Enter your work e-mail to sign in.",
  "ui.chooser.email": "Work e-mail",
  "ui.chooser.continue": "Continue",
  "ui.chooser.pickIntro": "{email} belongs to more than one organisation. Choose where to sign in.",
  "ui.chooser.notFound": "We could not find an organisation for {domain}. Check the address, or enter your organisation's short name.",
  "ui.chooser.manualIntro": "Enter your organisation's short name to continue.",
  "ui.chooser.organisation": "Organisation",
  "ui.chooser.unknownOrganisation": "Unknown organisation",
  "ui.chooser.useManual": "Sign in with organisation name",
  "ui.chooser.useEmail": "Sign in with e-mail instead",
  "ui.chooser.back": "Use another e-mail",
  "ui.chooser.signup": "Request a new organisation",
  "ui.chooser.hosting": "Hosting administration",
};
